import React, { useMemo } from 'react';
import {
  Box,
  Card,
  LineChart,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { InfoOutlined } from '@barracuda-internal/bds-core/dist/Icons/Core';

/**
 * Tiny status sparkline used in the right column next to grouped KPI
 * cards or the Geo Location section. Mirrors the "Requests Status /
 * Blocking Status / Query Per Second" pattern from the reference: title
 * on the left, "Max XXX" on the right, single line below with no axes
 * or legend.
 *
 * Props:
 *   title:  label
 *   data:   array of numbers (oldest -> newest)
 *   color:  borderColor token
 *   fill:   backgroundColor token (alpha-pre-set, e.g. Range20)
 *   height: chart height in px. Use "auto" to let the chart fill its
 *           parent (useful when this card sits next to a tall sibling
 *           and should stretch). Default 140.
 *   area:   true (default) = filled area chart; false = pure line. Pure
 *           lines feel lighter and read as "tracking" rather than
 *           "volume" — see the reference's Requests Status / Blocking
 *           Status panels.
 */
const MiniStatusChart = ({
  title,
  data,
  color,
  fill,
  height = 140,
  area = true,
}) => {
  const tokens = useTokens();

  const max = useMemo(
    () => (Array.isArray(data) && data.length ? Math.max(...data) : 0),
    [data]
  );

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const strokeColor = color || tokens.chartsColorSeries2Range100;
    return {
      labels: data.map((_, i) => i),
      datasets: [
        {
          label: title,
          data,
          borderColor: strokeColor,
          // When fill is disabled, Chart.js / BDS need a real
          // backgroundColor (not 'transparent') for the line to render
          // and the legend swatch to colour correctly. We mirror the
          // border colour so the swatch matches the stroke.
          backgroundColor: area
            ? fill || tokens.chartsColorSeries2Range20
            : strokeColor,
          fill: area,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: area ? 1.5 : 2,
        },
      ],
    };
  }, [data, title, color, fill, area, tokens]);

  const emptyState = { icon: <InfoOutlined />, text: 'No data yet.' };
  const hasEntries = max > 0;
  const isAuto = height === 'auto';
  const chartHeight = isAuto ? '100%' : `${height}px`;
  const chartBoxSx = isAuto
    ? { flex: 1, minHeight: 0, width: '100%' }
    : { height: `${height}px`, width: '100%' };

  return (
    <Card
      bdsType="staticSubtle"
      bdsBoxShadow="default"
      bdsBorderRadius="round"
      bdsPadding="default"
      sx={{
        height: isAuto ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 120ms ease',
        '&:hover': { transform: 'translateY(-1px)' },
      }}
    >
      <Stack
        sx={{
          gap: tokens.aliasesSpaceComponentDense,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: tokens.aliasesSpaceComponentDense,
          }}
        >
          <Typography variant="captionSubtle">{title}</Typography>
          <Typography variant="captionSubtle">
            Max{' '}
            <Typography component="span" variant="body1Bold">
              {max.toLocaleString()}
            </Typography>
          </Typography>
        </Stack>
        <Box sx={chartBoxSx}>
          {chartData && (
            <LineChart
              chartData={chartData}
              chartDimension={{ width: '100%', height: chartHeight }}
              hasEntries={hasEntries}
              emptyState={emptyState}
              hidePoints
              maintainAspectRatio={false}
              lineChartOptions={{
                interaction: { mode: 'index', intersect: false },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                      title: () => '',
                      label: (ctx) =>
                        `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
                    },
                  },
                },
                scales: {
                  x: { display: false },
                  y: { display: false },
                },
              }}
            />
          )}
        </Box>
      </Stack>
    </Card>
  );
};

export default MiniStatusChart;
